import createEnum from 'lib/createEnum';

// Events

/**
 * When availability changes, this event emits current availability.
 *
 * @event Gladly#availability:change
 * @param {Gladly.Availability} - Current availability
 */
export const AVAILABILITY_CHANGE_EVENT = 'availability:change';

/**
 * When a conversation is ended, this event is emitted.
 *
 * @event Gladly#conversation:ended
 * @prop {Gladly.Conversation_Ended_Reason} source - The reason the conversation was ended
 */
export const SESSION_ENDED_EVENT = 'conversation:ended';

/**
 * When a conversation is started, this event is emitted.
 *
 * @event Gladly#conversation:started
 */
export const SESSION_STARTED_EVENT = 'conversation:started';

/**
 * When a customer finishes onboarding, this event is emitted.
 *
 * @event Gladly#customer:onboarded
 * @prop {string} name - The customer's name entered
 * @prop {string} email - The customer's email address entered
 */
export const CUSTOMER_ONBOARDED_EVENT = 'customer:onboarded';

/**
 * When a message is received by the consumer, this event is emitted.
 *
 * @event Gladly#message:received
 * @prop {Gladly.Message_Type} type - Indicates the message type
 */
export const MESSAGE_RECEIVED_EVENT = 'message:received';

/**
 * When a message is sent by the consumer, this event is emitted.
 *
 * @event Gladly#message:sent
 * @prop {boolean} isInitialMessage - Indicates if this is the first message from the consumer in the conversation
 * @prop {Gladly.Message_Type} type - Indicates the message type
 */
export const MESSAGE_SENT_EVENT = 'message:sent';

/**
 * When a quick action is selected by the consumer, this event is emitted.
 *
 * @event Gladly#quick-action:selected
 * @prop {string} label - The label of the selected quick action
 * @prop {Gladly.Quick_Action_Type} type - The type of the selected quick action
 */
export const QUICK_ACTION_SELECTED = 'quick-action:selected';

/**
 * When a search result is selected by the consumer, this event is emitted.
 *
 * @event Gladly#search:result-selected
 * @prop {string} label - The label of the selected search result
 */
export const SEARCH_RESULT_SELECTED = 'search:result-selected';

/**
 * When the sidekick is closed by the consumer, this event is emitted.
 *
 * @event Gladly#sidekick:closed
 */
export const WIDGET_CLOSED_EVENT = 'sidekick:closed';

/**
 * When the sidekick is open by the consumer, this event is emitted.
 *
 * @event Gladly#sidekick:opened
 * @prop {Gladly.Sidekick_Open_Trigger} source - The way the sidekick was opened
 */
export const WIDGET_OPENED_EVENT = 'sidekick:opened';

//Enums

/**
 * The different availability states that exist:
 * - AVAILABLE: Chat agents are ready to receive new chat messages
 * - UNAVAILABLE_OFFICE_CLOSED: Chat is unavailable due to office hours
 * - UNAVAILABLE_BUSY: Chat is unavailable due to high demand
 *
 * @memberof Gladly
 * @typedef {string} Availability
 */
export const AVAILABILITY = createEnum('AVAILABLE', 'UNAVAILABLE_OFFICE_CLOSED', 'UNAVAILABLE_BUSY');

/**
 * The different conversation closed reasons that exist:
 * - AGENT_ENDED: The chat agent has ended the conversation
 * - TIME_OUT: There has been no activity in the chat within the configured time and conversation automatically closed
 * - SWITCH_DEVICES: The same customer has started a chat conversation on a different device
 *
 * @memberof Gladly
 * @typedef {string} Conversation_Ended_Reason
 */
export const CONVERSATION_ENDED_REASON = createEnum('AGENT_ENDED', 'TIME_OUT', 'SWITCH_DEVICES');

/**
 * The different message types that exist:
 * - TEXT: a message of type text
 * - IMAGE: a message of type image
 * - FILE: a message of type file
 *
 * @memberof Gladly
 * @typedef {string} Message_Type
 */
export const MESSAGE_TYPE = createEnum('TEXT', 'IMAGE', 'FILE');

/**
 * The different types of quick actions that exist:
 * - ANSWER: an answer given inside of the sidekick
 * - EXTERNAL_LINK: an external link that takes the customer to a new page
 *
 * @memberof Gladly
 * @typedef {string} Quick_Action_Type
 */
export const QUICK_ACTION_TYPE = createEnum('ANSWER', 'EXTERNAL_LINK');

/**
 * The different ways the sidekick can be opened that exist:
 * - API: via an API call
 * - BUTTON: via clicking on the chat icon
 * - PROACTIVE_RULE: via a proactive rule
 *
 * @memberof Gladly
 * @typedef {string} Sidekick_Open_Trigger

 */
export const WIDGET_OPENED_TRIGGER = createEnum('API', 'BUTTON', 'PROACTIVE_RULE');
